import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
  }).format(amount);
}

export function generateRegNo(prefix: string = 'PS'): string {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `${prefix}-${timestamp}${random}`;
}

export function safeFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9.-]/g, '_').toLowerCase();
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

export const ROLE_LABELS = {
  admin: 'Admin',
  teacher: 'Teacher',
} as const;

/* Vibrant status colors that catch the eye */
export const STATUS_COLORS = {
  active: 'bg-lime-100 dark:bg-lime-500/20 text-lime-700 dark:text-lime-300 border border-lime-200 dark:border-lime-500/30',
  drop: 'bg-tangerine-100 dark:bg-tangerine-500/20 text-tangerine-700 dark:text-tangerine-300 border border-tangerine-200 dark:border-tangerine-500/30',
  left: 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30',
} as const;

export const ATTENDANCE_COLORS = {
  present: 'bg-lime-100 dark:bg-lime-500/20 text-lime-700 dark:text-lime-300 border border-lime-200 dark:border-lime-500/30',
  absent: 'bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-500/30',
  late: 'bg-tangerine-100 dark:bg-tangerine-500/20 text-tangerine-700 dark:text-tangerine-300 border border-tangerine-200 dark:border-tangerine-500/30',
  leave: 'bg-electric-100 dark:bg-electric-500/20 text-electric-700 dark:text-electric-300 border border-electric-200 dark:border-electric-500/30',
} as const;

export const FEE_STATUS_COLORS = {
  paid: 'bg-lime-100 dark:bg-lime-500/20 text-lime-700 dark:text-lime-300 border border-lime-200 dark:border-lime-500/30',
  partial: 'bg-gold-100 dark:bg-gold-500/20 text-gold-700 dark:text-gold-300 border border-gold-200 dark:border-gold-500/30',
  due: 'bg-tangerine-100 dark:bg-tangerine-500/20 text-tangerine-700 dark:text-tangerine-300 border border-tangerine-200 dark:border-tangerine-500/30',
  overdue: 'bg-rose-100 dark:bg-rose-500/30 text-rose-800 dark:text-rose-200 border border-rose-200 dark:border-rose-500/40',
} as const;

/* Vibrant color palette for dashboard stats */
export const STAT_CARD_COLORS = [
  { bg: 'gradient-purple', icon: 'text-purple-400', glow: 'glow-purple' },
  { bg: 'gradient-magenta', icon: 'text-magenta-400', glow: 'glow-magenta' },
  { bg: 'gradient-electric', icon: 'text-electric-400', glow: 'glow-electric' },
  { bg: 'gradient-tangerine', icon: 'text-tangerine-400', glow: 'glow-tangerine' },
  { bg: 'gradient-lime', icon: 'text-lime-400', glow: 'glow-lime' },
  { bg: 'gradient-cyan', icon: 'text-cyan-400', glow: 'glow-cyan' },
  { bg: 'gradient-gold', icon: 'text-gold-400', glow: 'glow-gold' },
  { bg: 'gradient-rose', icon: 'text-rose-400', glow: 'glow-rose' },
];

/* Module color assignments for consistent theming */
export const MODULE_COLORS = {
  dashboard: { primary: 'purple', accent: 'magenta' },
  students: { primary: 'electric', accent: 'cyan' },
  admissions: { primary: 'magenta', accent: 'rose' },
  classes: { primary: 'tangerine', accent: 'gold' },
  sections: { primary: 'lime', accent: 'cyan' },
  batches: { primary: 'cyan', accent: 'electric' },
  teachers: { primary: 'violet', accent: 'purple' },
  fees: { primary: 'gold', accent: 'tangerine' },
  attendance: { primary: 'lime', accent: 'emerald' },
  documents: { primary: 'electric', accent: 'cyan' },
  ocr: { primary: 'magenta', accent: 'purple' },
  reports: { primary: 'tangerine', accent: 'gold' },
  settings: { primary: 'purple', accent: 'magenta' },
} as const;
